/* eslint-disable no-param-reassign */
/* eslint-disable react/no-unknown-property */
/* eslint-disable @altermaind/no-magic-numbers */
import { useRef, useState, useEffect, FC } from 'react';
import { useFrame } from '@react-three/fiber';
import { MathUtils, Group, Object3D, Mesh } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

type Props = {
  url: string;
  speaking?: boolean;
};

type MorphMesh = Mesh & {
  isSkinnedMesh: boolean;
  morphTargetDictionary: Record<string, number>;
  morphTargetInfluences: number[];
};

export const Avatar: FC<Props> = ({ url, speaking = false }) => {
  const group = useRef<Group>(null);
  const [model, setModel] = useState<Object3D | null>(null);
  const mouth = useRef<MorphMesh[]>([]);
  const eyes = useRef<MorphMesh[]>([]);
  const blinkState = useRef({
    isBlinking: false,
    blinkProgress: 0,
    nextBlink: 0,
  });

  useFrame((state, delta) => {
    if (!model) return;
    const t = state.clock.getElapsedTime();

    // Bocca
    mouth.current.forEach(mesh => {
      const idxOpen = mesh.morphTargetDictionary.mouthOpen;
      const idxSmile = mesh.morphTargetDictionary.mouthSmile;
      if (idxOpen !== undefined && idxSmile !== undefined) {
        if (speaking) {
          mesh.morphTargetInfluences[idxOpen] = 0.5 + 0.5 * Math.sin(t * 8 + mesh.id);
          mesh.morphTargetInfluences[idxSmile] = 0.3 + 0.3 * Math.sin(t * 5 + mesh.id);
        } else {
          mesh.morphTargetInfluences[idxOpen] = MathUtils.lerp(
            mesh.morphTargetInfluences[idxOpen],
            0,
            0.1,
          );
          mesh.morphTargetInfluences[idxSmile] = MathUtils.lerp(
            mesh.morphTargetInfluences[idxSmile],
            0,
            0.1,
          );
        }
      }
    });

    // Blink occhi
    if (t > blinkState.current.nextBlink && !blinkState.current.isBlinking) {
      blinkState.current.isBlinking = true;
      blinkState.current.blinkProgress = 0;
    }

    if (blinkState.current.isBlinking) {
      blinkState.current.blinkProgress += delta * 6;
      const progress = blinkState.current.blinkProgress;
      const blinkAmount = progress < 0.5 ? Math.sin(progress * Math.PI) : 0;

      eyes.current.forEach(mesh => {
        const idxL = mesh.morphTargetDictionary.eyeBlinkLeft;
        const idxR = mesh.morphTargetDictionary.eyeBlinkRight;
        if (idxL !== undefined) mesh.morphTargetInfluences[idxL] = Math.min(blinkAmount, 1);
        if (idxR !== undefined) mesh.morphTargetInfluences[idxR] = Math.min(blinkAmount, 1);
      });

      if (progress >= 1) {
        blinkState.current.isBlinking = false;
        blinkState.current.blinkProgress = 0;
        blinkState.current.nextBlink = t + 2 + Math.random() * 3;
      }
    }

    // Movimento testa
    if (group.current) {
      const rotationY = speaking
        ? Math.sin(t * 2) * 0.08
        : Math.sin(t * 0.5 + Math.random() * 0.2) * 0.02 +
          Math.sin(t * 0.3 + Math.random() * 0.2) * 0.015;

      group.current.rotation.y = MathUtils.lerp(group.current.rotation.y, rotationY, 0.05);
      group.current.rotation.x = MathUtils.lerp(
        group.current.rotation.x,
        Math.sin(t * 0.3) * 0.01,
        0.05,
      );
    }
  });

  useEffect(() => {
    const loader = new GLTFLoader();
    loader.crossOrigin = 'anonymous';
    loader.load(
      url,
      gltf => {
        const { scene } = gltf;

        mouth.current = [];
        eyes.current = [];

        scene.traverse(obj => {
          // Cast a MorphMesh per sicurezza TypeScript
          const mesh = obj as MorphMesh;
          if (mesh.isSkinnedMesh && mesh.morphTargetDictionary && mesh.morphTargetInfluences) {
            if (mesh.morphTargetDictionary.mouthOpen !== undefined) {
              mouth.current.push(mesh);
            }
            if (
              mesh.morphTargetDictionary.eyeBlinkLeft !== undefined ||
              mesh.morphTargetDictionary.eyeBlinkRight !== undefined
            ) {
              eyes.current.push(mesh);
            }
          }
        });

        const wrapper = new Group();
        wrapper.add(scene);
        wrapper.position.set(0, -3.6, 0.4);
        wrapper.scale.set(6.0, 6.0, 6.0);

        setModel(wrapper);
      },
      undefined,
      () => {
        // TODO: manage error
      },
    );
  }, [url]);

  if (!model) return null;

  return <primitive ref={group} object={model} />;
};
